import {
  calculateWorkDayStart,
  getNextDateTimeWithMatchingStepSize,
} from "@/data/models/SchedulingSettings";
import {
  Task,
  FocusedTask,
  getMinStartDateTimeForTaskType,
} from "@/data/models/Task";
import { SchedulingModule } from "@/store/modules/SchedulingModule";
import { DateTime } from "luxon";
import {
  areDatesOnSameDay,
  getDateFromHoursAndMinuteString,
  getDateFromYearMonthDateString,
} from "../DateHelper";
import { TaskScheduler } from "./TaskScheduler";
import { TimeSlot, getDayId } from "./TimeSlot";

function isTimeslotValidForTask(timeSlot: TimeSlot, task: Task) {
  const dateOfTimeSlot = new Date(timeSlot.startTimestamp);

  const {
    targetDate,
    targetTime,

    isTargetDateFixed,
    isTargetTimeFixed,
  } = task.planningParameters;

  if (isTargetDateFixed && targetDate) {
    const target = getDateFromYearMonthDateString(targetDate);

    if (target && !areDatesOnSameDay(target, dateOfTimeSlot)) {
      return false;
    }
  }

  if (isTargetTimeFixed && targetTime) {
    const target = getDateFromHoursAndMinuteString(targetTime);

    if (
      target &&
      !(
        target.getMinutes() == dateOfTimeSlot.getMinutes() &&
        target.getHours() == dateOfTimeSlot.getHours()
      )
    ) {
      return false;
    }
  }

  return true;
}

export function findNextFittingTimeSlot(
  userId: string,
  taskId: string,
  startingFrom: Date,
  focusedTask: FocusedTask
): TimeSlot | null {
  const includeWeekends = SchedulingModule.timelineSettings.showWeekends;

  const startDate = getNextDateTimeWithMatchingStepSize(
    getMinStartDateTimeForTaskType(
      DateTime.fromJSDate(startingFrom),
      focusedTask.taskType,
      includeWeekends
    ),
    SchedulingModule.schedulingSettings.stepSizeInMin
  ).toJSDate();

  const timeSlot: TimeSlot = {
    startTimestamp: startDate.getTime(),
    durationInMs: focusedTask.durationInMin * 60000,
    dayId: getDayId(startDate),
  };

  const tasksOfUser = SchedulingModule.tasksOfUsersLookup.get(userId);
  const fittingTimeSlot = tasksOfUser
    ? tasksOfUser.findFittingTimeSlot(timeSlot, taskId)
    : timeSlot;

  const now = new Date();

  if (
    fittingTimeSlot &&
    isTimeslotValidForTask(timeSlot, focusedTask) &&
    fittingTimeSlot.startTimestamp > now.getTime()
  ) {
    const fittedStartDate = new Date(fittingTimeSlot.startTimestamp);
    const fittedEndDate = new Date(
      fittingTimeSlot.startTimestamp + fittingTimeSlot.durationInMs
    );

    return {
      dayId: getDayId(fittedStartDate),
      startTimestamp: fittedStartDate.getTime(),
      durationInMs: fittedEndDate.getTime() - fittedStartDate.getTime(),
    };
  } else {
    const scheduler = new TaskScheduler(
      [
        ...SchedulingModule.scheduledTasks.filter(
          (schedueledTask) => schedueledTask.id !== focusedTask.id
        ),
      ],
      SchedulingModule.sortedUsers,
      SchedulingModule.userLookup,
      SchedulingModule.schedulingSettings
    );

    const dateTime = DateTime.fromJSDate(startDate);
    const schedulingSettings = SchedulingModule.schedulingSettings;
    const roundedStartDate = getNextDateTimeWithMatchingStepSize(
      dateTime,
      schedulingSettings.stepSizeInMin
    );

    const workDayStart = calculateWorkDayStart(
      dateTime,
      schedulingSettings.workingHoursSettings.defaultWorkDayStart,
      schedulingSettings.travelTimeSettings
        .defaultTravelTimeBetweenTaskAndDepotInMin,
      schedulingSettings.stepSizeInMin
    );

    const startingFrom = focusedTask.planningParameters.isTargetDateFixed
      ? undefined
      : workDayStart.valueOf() > roundedStartDate.valueOf()
      ? workDayStart
      : roundedStartDate;

    const schedulingResult = scheduler.scheduleTasks(
      [
        {
          ...focusedTask,
        },
      ],
      !SchedulingModule.timelineSettings.showWeekends,
      {
        forcedStartDate: startingFrom?.toJSDate(),
        forcedUserId: userId,
      }
    )[0];

    if (schedulingResult) {
      const scheduledTask = schedulingResult.scheduledTask;

      return {
        dayId: getDayId(scheduledTask.startDate),
        startTimestamp: scheduledTask.startDate.getTime(),
        durationInMs:
          scheduledTask.endDate.getTime() - scheduledTask.startDate.getTime(),
        schedulingScore: scheduledTask.schedulingScore,
      };
    } else {
      return null;
    }
  }
}
